'use client';
import React, { Suspense, useMemo } from 'react';
import { DateTime } from 'luxon';
import { SectionTitle } from '@benzinga/core-ui';
import { numberShorthand } from '@benzinga/utils/numberUtils';

import type { BaseQuotePageProps } from '../../../../../src/utils/quote/types';
import type { InsiderTradesSummary } from '../../../../../src/utils/quote/summaries';
import QuotePageTitleLink from '../_components/QuotePageTitleLink';
import { QuoteFooter } from '../../../../../src/components/Quote/QuoteFooter';
import { KPIBox } from '../../../../../src/components/Quote/KPIBox';
import { formatTradeValue } from '../../../../../src/components/Quote/formatters';
import InsiderFilingsTable from '../../../../../src/components/Quote/InsiderFilingsTable';
import GovTradesTable from '../../../../../src/components/Quote/GovTradesTable';
import type { Security as GovSecurity } from '@benzinga/gov-trades';
import PeopleAlsoWatch from '../_components/PeopleAlsoWatch';
import { ChartPlotSkeleton } from '../../../../../src/components/Quote/QuoteSkeletons';
import type { EChartsOption } from 'echarts';

const ReactECharts = React.lazy(() => import('echarts-for-react'));

interface InsiderOwnership {
  insiderPercentOwned: number | null;
  insiderSharesOwned: number | null;
  institutionPercentHeld: number | null;
  institutionSharesHeld: number | null;
  sharesFloat: number | null;
  sharesOutstanding: number | null;
}

const OwnershipDonutChart: React.FC<{ ownership: InsiderOwnership }> = ({ ownership }) => {
  const insiderPct = ownership.insiderPercentOwned != null ? ownership.insiderPercentOwned * 100 : null;
  const institutionPct = ownership.institutionPercentHeld != null ? ownership.institutionPercentHeld * 100 : null;
  const option = useMemo((): EChartsOption => {
    if (insiderPct == null && institutionPct == null) return {} as EChartsOption;
    const insider = insiderPct ?? 0;
    const institutional = institutionPct ?? 0;
    const bothKnown = insiderPct != null && institutionPct != null;
    const remainder = Math.max(0, 100 - insider - institutional);
    return {
      tooltip: {
        trigger: 'item',
        formatter: (params: { name?: string; value?: number }) => `${params.name}: ${params.value}%`,
      },
      legend: { orient: 'vertical', right: 8, top: 'center', textStyle: { fontSize: 11 } },
      series: [
        {
          type: 'pie',
          radius: ['45%', '70%'],
          center: ['35%', '50%'],
          label: { show: false },
          data: [
            { value: Number(institutional.toFixed(1)), name: 'Institutional', itemStyle: { color: '#4B8BF4' } },
            { value: Number(remainder.toFixed(1)), name: bothKnown ? 'Retail' : 'Other', itemStyle: { color: '#9CA3AF' } },
            { value: Number(insider.toFixed(1)), name: 'Insider', itemStyle: { color: '#f2994a' } },
          ].filter(slice => slice.value > 0),
        },
      ],
    } as EChartsOption;
  }, [insiderPct, institutionPct]);

  if (insiderPct == null && institutionPct == null) return null;
  return (
    <div className="rounded border border-[#e9ecef] bg-white p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Ownership Breakdown</p>
      <Suspense fallback={<ChartPlotSkeleton label="Loading ownership chart" />}>
        <ReactECharts option={option} style={{ height: 220 }} />
      </Suspense>
    </div>
  );
};

const NetInsiderActivityChart: React.FC<{ filings: InsiderTradesSummary['filings'] }> = ({ filings }) => {
  const option = useMemo((): EChartsOption => {
    const monthMap = new Map<string, { buy: number; sell: number }>();
    const cutoff = DateTime.now().minus({ months: 12 });

    for (const filing of filings) {
      const dateStr = filing.first_trade_date || filing.last_trade_date;
      if (!dateStr) continue;
      const tradeDate = DateTime.fromISO(dateStr);
      if (tradeDate < cutoff) continue;
      const monthKey = tradeDate.toFormat('yyyy-MM');
      const prev = monthMap.get(monthKey) ?? { buy: 0, sell: 0 };
      const value = Math.abs(filing.traded_value ?? 0);
      if (filing.trade_status === 'BUY') {
        monthMap.set(monthKey, { ...prev, buy: prev.buy + value });
      } else if (filing.trade_status === 'SELL') {
        monthMap.set(monthKey, { ...prev, sell: prev.sell + value });
      }
    }

    const sortedKeys = Array.from(monthMap.keys()).sort();
    const labels = sortedKeys.map(monthKey => DateTime.fromFormat(monthKey, 'yyyy-MM').toFormat('MMM yy'));
    const buyData = sortedKeys.map(monthKey => monthMap.get(monthKey)!.buy);
    const sellData = sortedKeys.map(monthKey => -monthMap.get(monthKey)!.sell);

    return {
      grid: { left: 60, right: 16, top: 8, bottom: 56 },
      legend: { data: ['Purchases', 'Sales'], bottom: 0, textStyle: { fontSize: 10 } },
      tooltip: {
        trigger: 'axis',
        formatter: (params: unknown) => {
          const rows = Array.isArray(params) ? params : [params];
          const axisValue = (rows[0] as { axisValue?: string })?.axisValue ?? '';
          const body = rows
            .map(rowParam => {
              const row = rowParam as { marker: string; seriesName: string; value: number };
              return `${row.marker}${row.seriesName}: ${formatTradeValue(Math.abs(row.value))}`;
            })
            .join('<br/>');
          return `<b>${axisValue}</b><br/>${body}`;
        },
      },
      xAxis: { type: 'category', data: labels, axisLabel: { fontSize: 10 } },
      yAxis: {
        type: 'value',
        axisLabel: { fontSize: 10, formatter: (value: number) => formatTradeValue(Math.abs(value)) },
        splitLine: { lineStyle: { color: '#f0f0f0' } },
      },
      series: [
        {
          name: 'Purchases',
          type: 'bar',
          data: buyData,
          itemStyle: { color: '#30BF60' },
        },
        {
          name: 'Sales',
          type: 'bar',
          data: sellData,
          itemStyle: { color: '#E05252' },
        },
      ],
    } as EChartsOption;
  }, [filings]);

  if (!filings.length) return null;
  return (
    <div className="rounded border border-[#e9ecef] bg-white p-4">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Net Insider Activity by Month</p>
      <Suspense fallback={<ChartPlotSkeleton label="Loading insider activity chart" />}>
        <ReactECharts option={option} style={{ height: 220 }} />
      </Suspense>
    </div>
  );
};

const FreestarAd = React.lazy(() =>
  import('@benzinga/ads').then(module => ({ default: module.FreestarAd })),
);

interface OwnershipContentProps extends BaseQuotePageProps {
  insiderOwnershipSummary: InsiderOwnership;
  insiderTradesSummary: InsiderTradesSummary | null;
  govTradesResponse?: GovSecurity | null;
  memberProfileSlugs: Record<string, string>;
  insiderNews?: unknown[];
  fundamentals?: unknown;
}

const OwnershipContent: React.FC<OwnershipContentProps> = ({
  govTradesResponse,
  insiderOwnershipSummary,
  insiderTradesSummary,
  memberProfileSlugs,
  metaProps,
  profile,
  symbol,
}) => {
  const companyName = profile?.richQuoteData?.name ?? symbol;
  const { buys90, sells90, buyValue90, sellValue90 } = useMemo(() => {
    const filings = insiderTradesSummary?.filings ?? [];
    const cutoff = DateTime.now().minus({ days: 90 });
    const recent = filings.filter(f => {
      const d = f.first_trade_date || f.last_trade_date;
      return d ? DateTime.fromISO(d) >= cutoff : false;
    });
    const buys = recent.filter(f => f.trade_status === 'BUY');
    const sells = recent.filter(f => f.trade_status === 'SELL');
    return {
      buys90: buys.length,
      sells90: sells.length,
      buyValue90: buys.reduce((sum, filing) => sum + Math.abs(filing.traded_value ?? 0), 0),
      sellValue90: sells.reduce((sum, filing) => sum + Math.abs(filing.traded_value ?? 0), 0),
    };
  }, [insiderTradesSummary]);

  const insiderPct = insiderOwnershipSummary.insiderPercentOwned;
  const institutionPct = insiderOwnershipSummary.institutionPercentHeld;
  return (
    <>
      <div className="main-div">
        <div className="flex flex-col gap-4 lg:flex-row">
          <div className="flex flex-col gap-y-8 w-full lg:w-9/12">
            <div className="flex flex-col gap-2">
              <SectionTitle level={1} uppercase={false}>
                <QuotePageTitleLink title={metaProps.title} symbol={symbol} />
              </SectionTitle>
              <p className="section-description">{metaProps.description}</p>
            </div>

            {(insiderPct != null || institutionPct != null) && (
              <div className="rounded border border-[#e9ecef] bg-white p-4 text-sm text-gray-700 leading-relaxed">
                {insiderPct != null && (
                  <span>
                    Insiders own <strong>{(insiderPct * 100).toFixed(1)}%</strong> of shares outstanding.{' '}
                  </span>
                )}
                {institutionPct != null && (
                  <span>
                    Institutional investors hold <strong>{(institutionPct * 100).toFixed(1)}%</strong>.{' '}
                  </span>
                )}
                {(buys90 > 0 || sells90 > 0) && (
                  <span>
                    Over the past 90 days, insiders made{' '}
                    {buys90 > 0 && (
                      <>
                        <strong className="text-green-700">
                          {buys90} purchase{buys90 !== 1 ? 's' : ''}
                        </strong>
                        {buyValue90 > 0 && ` worth ${formatTradeValue(buyValue90)}`}
                      </>
                    )}
                    {buys90 > 0 && sells90 > 0 && ' and '}
                    {sells90 > 0 && (
                      <>
                        <strong className="text-red-600">
                          {sells90} sale{sells90 !== 1 ? 's' : ''}
                        </strong>
                        {sellValue90 > 0 && ` worth ${formatTradeValue(sellValue90)}`}
                      </>
                    )}
                    .
                  </span>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:col-span-2">
                {insiderOwnershipSummary.sharesOutstanding != null && (
                  <KPIBox label="Shares Outstanding" value={numberShorthand(insiderOwnershipSummary.sharesOutstanding)} />
                )}
                {insiderOwnershipSummary.sharesFloat != null && (
                  <KPIBox label="Shares Float" value={numberShorthand(insiderOwnershipSummary.sharesFloat)} />
                )}
                {insiderOwnershipSummary.insiderSharesOwned != null && (
                  <KPIBox label="Insider Shares Owned" value={numberShorthand(insiderOwnershipSummary.insiderSharesOwned)} />
                )}
                {insiderOwnershipSummary.insiderPercentOwned != null && (
                  <KPIBox label="Insider % Owned" value={`${(insiderOwnershipSummary.insiderPercentOwned * 100).toFixed(1)}%`} />
                )}
                {insiderOwnershipSummary.institutionSharesHeld != null && (
                  <KPIBox label="Institutional Shares Held" value={numberShorthand(insiderOwnershipSummary.institutionSharesHeld)} />
                )}
                {insiderOwnershipSummary.institutionPercentHeld != null && (
                  <KPIBox label="Institutional % Held" value={`${(insiderOwnershipSummary.institutionPercentHeld * 100).toFixed(1)}%`} />
                )}
              </div>
              <OwnershipDonutChart ownership={insiderOwnershipSummary} />
            </div>

            {(insiderTradesSummary?.filings?.length ?? 0) > 0 && (
              <NetInsiderActivityChart filings={insiderTradesSummary!.filings} />
            )}

            {(buys90 > 0 || sells90 > 0) && (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <KPIBox
                  label="Purchases (90d)"
                  value={String(buys90)}
                  valueTone="positive"
                  sub={buyValue90 > 0 ? `${formatTradeValue(buyValue90)} total` : undefined}
                />
                <KPIBox
                  label="Sales (90d)"
                  value={String(sells90)}
                  valueTone="negative"
                  sub={sellValue90 > 0 ? `${formatTradeValue(sellValue90)} total` : undefined}
                />
                <KPIBox
                  label="Net Activity"
                  value={
                    buyValue90 > sellValue90 ? 'Net Buying' : sellValue90 > buyValue90 ? 'Net Selling' : 'Neutral'
                  }
                  valueTone={
                    buyValue90 > sellValue90 ? 'positive' : sellValue90 > buyValue90 ? 'negative' : 'neutral'
                  }
                />
                <KPIBox
                  label="Net Value (90d)"
                  value={formatTradeValue(buyValue90 - sellValue90)}
                  valueTone={buyValue90 >= sellValue90 ? 'positive' : 'negative'}
                />
              </div>
            )}

            {(insiderTradesSummary?.filings?.length ?? 0) > 0 && (
              <InsiderFilingsTable
                companyName={companyName}
                filings={insiderTradesSummary!.filings}
                symbol={symbol}
              />
            )}

            {govTradesResponse && (
              <GovTradesTable
                memberProfileSlugs={memberProfileSlugs}
                symbol={symbol}
                trades={govTradesResponse.stats?.recent_trades ?? []}
              />
            )}

            <Suspense fallback={<div />}>
              <FreestarAd className="w-[300px] min-w-[300px]" onlyMobile={true} placement="incontent" />
            </Suspense>

            <QuoteFooter activeTab="ownership" />
          </div>

          <PeopleAlsoWatch profile={profile} symbol={symbol}>
            <Suspense fallback={<div />}>
              <FreestarAd className="w-[300px] min-w-[300px]" onlyDesktop={true} placement="sidebar_2" />
            </Suspense>
          </PeopleAlsoWatch>
        </div>
      </div>
    </>
  );
};

export default OwnershipContent;
